/**
 * Health Integration Service
 * Integrates health data with challenge progress and provides unified health functionality
 */

import { FirebaseService } from './FirebaseService';
import { ChallengeService } from './ChallengeService';
import { HealthService } from './HealthService';
import {
    HealthSummary,
    HealthPermissions,
    WorkoutData,
    SleepData,
    HeartRateData,
    WeightData,
    BloodPressureData,
    HealthServiceConfig,
    Challenge,
    ChallengeParticipation,
    ApiResponse,
    HealthData
} from '../types';
import { getTodayISO } from '../utils/dateUtils';

export class HealthIntegrationService {
    private firebaseService: FirebaseService;
    private challengeService: ChallengeService;
    private healthService: HealthService;

    constructor(
        firebaseService: FirebaseService,
        challengeService: ChallengeService,
        healthService: HealthService
    ) {
        this.firebaseService = firebaseService;
        this.challengeService = challengeService;
        this.healthService = healthService;
    }

    // ============================================================================
    // HEALTH SERVICE DELEGATION
    // ============================================================================

    async initialize(): Promise<ApiResponse<void>> {
        return this.healthService.initialize();
    }

    async requestPermissions(permissions: Partial<HealthPermissions>): Promise<ApiResponse<HealthPermissions>> {
        return this.healthService.requestPermissions(permissions);
    }

    async checkPermissions(): Promise<ApiResponse<HealthPermissions>> {
        return this.healthService.checkPermissions();
    }

    async getHealthSummary(date: Date): Promise<ApiResponse<HealthSummary>> {
        return this.healthService.getHealthSummary(date);
    }

    async getHealthSummaryRange(startDate: Date, endDate: Date): Promise<ApiResponse<HealthSummary[]>> {
        return this.healthService.getHealthSummaryRange(startDate, endDate);
    }

    async getSteps(date: Date): Promise<ApiResponse<number>> {
        return this.healthService.getSteps(date);
    }

    async getCalories(date: Date): Promise<ApiResponse<number>> {
        return this.healthService.getCalories(date);
    }

    async getDistance(date: Date): Promise<ApiResponse<number>> {
        return this.healthService.getDistance(date);
    }

    async getActiveMinutes(date: Date): Promise<ApiResponse<number>> {
        return this.healthService.getActiveMinutes(date);
    }

    async getWorkouts(date: Date): Promise<ApiResponse<WorkoutData[]>> {
        return this.healthService.getWorkouts(date);
    }

    async getSleep(date: Date): Promise<ApiResponse<SleepData | null>> {
        return this.healthService.getSleep(date);
    }

    async getHeartRate(date: Date): Promise<ApiResponse<HeartRateData[]>> {
        return this.healthService.getHeartRate(date);
    }

    async getWeight(date: Date): Promise<ApiResponse<WeightData | null>> {
        return this.healthService.getWeight(date);
    }

    async getBloodPressure(date: Date): Promise<ApiResponse<BloodPressureData | null>> {
        return this.healthService.getBloodPressure(date);
    }

    async saveWorkout(workout: Omit<WorkoutData, 'id' | 'source'>): Promise<ApiResponse<string>> {
        return this.healthService.saveWorkout(workout);
    }

    async saveSleep(sleep: Omit<SleepData, 'id' | 'source'>): Promise<ApiResponse<string>> {
        return this.healthService.saveSleep(sleep);
    }

    async saveWeight(weight: Omit<WeightData, 'id' | 'source'>): Promise<ApiResponse<string>> {
        return this.healthService.saveWeight(weight);
    }

    async saveBloodPressure(bp: Omit<BloodPressureData, 'id' | 'source'>): Promise<ApiResponse<string>> {
        return this.healthService.saveBloodPressure(bp);
    }

    async startObserving(callback: (data: HealthSummary) => void): Promise<ApiResponse<void>> {
        return this.healthService.startObserving(callback);
    }

    async stopObserving(): Promise<ApiResponse<void>> {
        return this.healthService.stopObserving();
    }

    async testHealthKitAvailability(): Promise<{ available: boolean; error?: string }> {
        return this.healthService.testHealthKitAvailability();
    }

    isAvailable(): boolean {
        return this.healthService.isAvailable();
    }

    getConfig(): HealthServiceConfig {
        return this.healthService.getConfig();
    }

    async updateConfig(config: Partial<HealthServiceConfig>): Promise<ApiResponse<void>> {
        return this.healthService.updateConfig(config);
    }

    // ============================================================================
    // HEALTH DATA PERSISTENCE
    // ============================================================================

    /**
     * Save health data to Firebase
     */
    async saveHealthData(userId: string, healthData: HealthData): Promise<ApiResponse<void>> {
        try {
            await this.firebaseService.saveHealthData(healthData);
            return { success: true };
        } catch (error) {
            console.error('❌ Error saving health data:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to save health data'
            };
        }
    }

    /**
     * Load health data from Firebase
     */
    async loadHealthData(
        userId: string,
        startDate: string,
        endDate: string
    ): Promise<HealthData[]> {
        try {
            return await this.firebaseService.loadHealthData(userId, startDate, endDate);
        } catch (error) {
            console.error('❌ Error loading health data:', error);
            throw error;
        }
    }

    /**
     * Sync health data from platform to Firebase
     */
    async syncHealthData(userId: string, date: Date = new Date()): Promise<ApiResponse<void>> {
        try {
            const healthSummary = await this.healthService.getHealthSummary(date);
            if (!healthSummary.success || !healthSummary.data) {
                return {
                    success: false,
                    error: 'Failed to get health summary from platform'
                };
            }

            const healthData: HealthData = {
                userId,
                date: date.toISOString().split('T')[0],
                steps: healthSummary.data.steps,
                calories: healthSummary.data.calories,
                distance: healthSummary.data.distance,
                activeMinutes: healthSummary.data.activeMinutes,
                lastUpdated: new Date().toISOString()
            };

            await this.firebaseService.saveHealthData(healthData);
            return { success: true };
        } catch (error) {
            console.error('❌ Error syncing health data:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to sync health data'
            };
        }
    }

    // ============================================================================
    // CHALLENGE INTEGRATION
    // ============================================================================

    /**
     * Auto-update challenge progress based on health data
     */
    async autoUpdateChallengeProgress(userId: string, date: Date = new Date()): Promise<ApiResponse<void>> {
        try {
            const [activeChallenges, healthSummary] = await Promise.all([
                this.challengeService.getActiveChallenges(userId),
                this.healthService.getHealthSummary(date)
            ]);

            if (!healthSummary.success || !healthSummary.data) {
                return {
                    success: false,
                    error: 'Failed to get health summary'
                };
            }

            const updatePromises: Promise<ApiResponse<void>>[] = [];

            for (const challenge of activeChallenges) {
                let value: number | undefined;

                switch (challenge.metric) {
                    case 'steps':
                        value = healthSummary.data.steps;
                        break;
                    case 'calories':
                        value = healthSummary.data.calories;
                        break;
                    case 'distance':
                        value = healthSummary.data.distance;
                        break;
                    case 'time':
                        value = healthSummary.data.activeMinutes;
                        break;
                    default:
                        continue; // Skip challenges that don't use health data
                }

                if (value !== undefined && value > 0) {
                    updatePromises.push(
                        this.challengeService.updateProgress(userId, challenge.id, value)
                    );
                }
            }

            const results = await Promise.all(updatePromises);
            const hasErrors = results.some(result => !result.success);

            if (hasErrors) {
                return {
                    success: false,
                    error: 'Some challenge updates failed'
                };
            }

            return { success: true };
        } catch (error) {
            console.error('❌ Error auto-updating challenge progress:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to auto-update challenge progress'
            };
        }
    }

    /**
     * Get health-based challenge recommendations
     */
    async getHealthBasedRecommendations(userId: string): Promise<Challenge[]> {
        try {
            const [allChallenges, healthSummary] = await Promise.all([
                this.challengeService.getUserChallenges(userId),
                this.healthService.getHealthSummary(new Date())
            ]);

            if (!healthSummary.success || !healthSummary.data) {
                return [];
            }

            // Filter challenges based on health data
            const recommendations = allChallenges.filter(challenge => {
                if (!challenge.isEligible) return false;

                // Recommend challenges that match current health activity
                switch (challenge.metric) {
                    case 'steps':
                        return (healthSummary.data?.steps ?? 0) > 0;
                    case 'calories':
                        return (healthSummary.data?.calories ?? 0) > 0;
                    case 'distance':
                        return (healthSummary.data?.distance ?? 0) > 0;
                    case 'time':
                        return (healthSummary.data?.activeMinutes ?? 0) > 0;
                    default:
                        return true;
                }
            });

            // Sort by relevance (personal challenges first, then by points)
            return recommendations.sort((a, b) => {
                if (a.level === 'personal' && b.level !== 'personal') return -1;
                if (b.level === 'personal' && a.level !== 'personal') return 1;
                return b.points - a.points;
            });
        } catch (error) {
            console.error('❌ Error getting health-based recommendations:', error);
            return [];
        }
    }

    /**
     * Get health insights for challenges
     */
    async getHealthInsights(userId: string, challengeId: string): Promise<{
        currentHealthValue: number;
        targetValue: number;
        progress: number;
        healthTrend: 'increasing' | 'decreasing' | 'stable';
        recommendations: string[];
    } | null> {
        try {
            const [challenge, participation, healthSummary] = await Promise.all([
                this.firebaseService.getChallenge(challengeId),
                this.firebaseService.getParticipation(userId, challengeId),
                this.healthService.getHealthSummary(new Date())
            ]);

            if (!challenge || !participation || !healthSummary.success || !healthSummary.data) {
                return null;
            }

            let currentHealthValue = 0;
            switch (challenge.metric) {
                case 'steps':
                    currentHealthValue = healthSummary.data.steps;
                    break;
                case 'calories':
                    currentHealthValue = healthSummary.data.calories;
                    break;
                case 'distance':
                    currentHealthValue = healthSummary.data.distance;
                    break;
                case 'time':
                    currentHealthValue = healthSummary.data.activeMinutes;
                    break;
                default:
                    return null;
            }

            // Helper functions to get target value and unit from new or legacy fields
            const getTargetValue = (challenge: any) => challenge.measurementTarget || challenge.targetValue;
            const getTargetUnit = (challenge: any) => challenge.measurementUnit || challenge.targetUnit;

            // Calculate progress and remaining
            const targetValue = getTargetValue(challenge);
            const progress = Math.min(100, (currentHealthValue / targetValue) * 100);
            const remaining = Math.max(0, targetValue - currentHealthValue);

            // Simple trend analysis (could be enhanced with historical data)
            const healthTrend: 'increasing' | 'decreasing' | 'stable' =
                currentHealthValue > participation.currentValue ? 'increasing' :
                    currentHealthValue < participation.currentValue ? 'decreasing' : 'stable';

            const recommendations: string[] = [];

            if (progress < 50) {
                recommendations.push('You\'re off to a good start! Try to increase your daily activity.');
            } else if (progress < 80) {
                recommendations.push('Great progress! You\'re more than halfway there.');
            } else if (progress < 100) {
                recommendations.push('Almost there! Keep up the momentum to reach your goal.');
            } else {
                recommendations.push('Congratulations! You\'ve exceeded your target.');
            }

            if (remaining > 0) {
                const dailyTarget = remaining / Math.max(1, Math.ceil((new Date(challenge.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                recommendations.push(`Aim for ${Math.round(dailyTarget)} ${challenge.metric} per day to reach your goal.`);
            }

            return {
                currentHealthValue,
                targetValue: targetValue,
                progress,
                healthTrend,
                recommendations
            };
        } catch (error) {
            console.error('❌ Error getting health insights:', error);
            return null;
        }
    }

    // ============================================================================
    // HEALTH ANALYTICS
    // ============================================================================

    /**
     * Get health statistics for a date range
     */
    async getHealthStats(
        userId: string,
        startDate: Date,
        endDate: Date
    ): Promise<{
        totalSteps: number;
        totalCalories: number;
        totalDistance: number;
        totalActiveMinutes: number;
        averageStepsPerDay: number;
        averageCaloriesPerDay: number;
        averageDistancePerDay: number;
        averageActiveMinutesPerDay: number;
        daysWithData: number;
        bestDay: { date: string; steps: number; calories: number };
        challengeProgress: { completed: number; inProgress: number; total: number };
    }> {
        try {
            const [healthSummaries, participations] = await Promise.all([
                this.healthService.getHealthSummaryRange(startDate, endDate),
                this.firebaseService.loadUserParticipations(userId)
            ]);

            if (!healthSummaries.success || !healthSummaries.data) {
                return this.getDefaultHealthStats();
            }

            const summaries = healthSummaries.data;
            const daysWithData = summaries.length;

            const totalSteps = summaries.reduce((sum, day) => sum + day.steps, 0);
            const totalCalories = summaries.reduce((sum, day) => sum + day.calories, 0);
            const totalDistance = summaries.reduce((sum, day) => sum + day.distance, 0);
            const totalActiveMinutes = summaries.reduce((sum, day) => sum + day.activeMinutes, 0);

            const averageStepsPerDay = daysWithData > 0 ? totalSteps / daysWithData : 0;
            const averageCaloriesPerDay = daysWithData > 0 ? totalCalories / daysWithData : 0;
            const averageDistancePerDay = daysWithData > 0 ? totalDistance / daysWithData : 0;
            const averageActiveMinutesPerDay = daysWithData > 0 ? totalActiveMinutes / daysWithData : 0;

            // Find best day
            const bestDay = summaries.reduce((best, day) =>
                day.steps > best.steps ? { date: day.date, steps: day.steps, calories: day.calories } : best,
                { date: '', steps: 0, calories: 0 }
            );

            // Challenge progress
            const completed = participations.filter(p => p.status === 'completed').length;
            const inProgress = participations.filter(p => p.status === 'in_progress').length;
            const total = participations.length;

            return {
                totalSteps,
                totalCalories,
                totalDistance,
                totalActiveMinutes,
                averageStepsPerDay,
                averageCaloriesPerDay,
                averageDistancePerDay,
                averageActiveMinutesPerDay,
                daysWithData,
                bestDay,
                challengeProgress: { completed, inProgress, total }
            };
        } catch (error) {
            console.error('❌ Error getting health stats:', error);
            return this.getDefaultHealthStats();
        }
    }

    private getDefaultHealthStats() {
        return {
            totalSteps: 0,
            totalCalories: 0,
            totalDistance: 0,
            totalActiveMinutes: 0,
            averageStepsPerDay: 0,
            averageCaloriesPerDay: 0,
            averageDistancePerDay: 0,
            averageActiveMinutesPerDay: 0,
            daysWithData: 0,
            bestDay: { date: '', steps: 0, calories: 0 },
            challengeProgress: { completed: 0, inProgress: 0, total: 0 }
        };
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Get Firebase service instance
     */
    getFirebaseService(): FirebaseService {
        return this.firebaseService;
    }

    /**
     * Get Challenge service instance
     */
    getChallengeService(): ChallengeService {
        return this.challengeService;
    }

    /**
     * Get Health service instance
     */
    getHealthService(): HealthService {
        return this.healthService;
    }
} 